<template>
  <div class="c-explore-billing-plans">
    <h3 class="c-explore-billing-plans__title"> Explore Plans </h3>
  </div>
  <BittsLayout
    variant="equal"
    :ordered-slots="orderedSlots"
    class="mb-24"
    :gap="24"
    :min-col-width="305"
  >
    <template v-if="isNotPaying" #explorer>
      <BillingPlanComparisonCard tier="explorer" :is-billing-page="true">
        <template #action>
          <BittsButton
            text="Current plan"
            size="medium"
            type="neutral"
            variant="outline"
            class="w-full mx-auto bg-white"
            :disabled="true"
          />
        </template>
      </BillingPlanComparisonCard>
    </template>
    <template #connector>
      <BillingPlanComparisonCard
        :on-legacy-connector="isLegacyConnectorPPv5"
        :period="option"
        tier="connector"
        :is-current-tier="isConnectorTier"
        :is-billing-page="true"
      >
        <template #action>
          <BittsButton
            v-if="isSelfServeLegacy && !isOnTrial"
            text="Unlock platform features"
            size="medium"
            type="primary"
            :left-icon="['fak', 'unlock']"
            class="w-full mx-auto mb-8"
            @click="
              handleBillingInteraction({
                cta: PLAN_AND_BILLING_CTAS.EXPLORE_PLANS_PLATFORM,
                event_site: EVENT_SITES.EXPLORE_PLANS_FREE_TIER_BUTTON,
                period: option,
                isPlatformUpgrade: true,
              })
            "
          />
          <BittsButton
            :text="connectorButtonText"
            size="medium"
            type="primary"
            :variant="
              isNotPaying || isSubscriptionCancelled ? 'fill' : 'outline'
            "
            class="w-full mx-auto"
            :left-icon="connectorButtonIcon"
            :disabled="
              (!hasSubscription && isConnectorTier && !isOnTrial) ||
              (hasSubscription &&
                hasPaymentIssue &&
                connectorButtonText === 'Add seats')
            "
            :class="{
              'bg-white': !hasSubscription && isConnectorTier && !isOnTrial,
            }"
            @click="handleConnectorAction"
          />
        </template>
      </BillingPlanComparisonCard>
    </template>
    <template #supernode>
      <BillingPlanComparisonCard tier="supernode" :is-billing-page="true">
        <template #action>
          <BittsButton
            text="Talk to Sales"
            size="medium"
            type="upsell"
            :left-icon="['fak', 'upgrade']"
            class="w-full mx-auto"
            @click="
              handleBillingInteraction({
                talkToSalesReason: 'Upgrade to Supernode',
                event_site: EVENT_SITES.EXPLORE_PLANS_SUPERNODE_BUTTON,
              })
            "
          />
        </template>
      </BillingPlanComparisonCard>
    </template>
    <template v-if="!isNotPaying" #enterprise>
      <BillingPlanComparisonCard tier="enterprise" :is-billing-page="true">
        <template #action>
          <BittsButton
            text="Talk to Sales"
            size="medium"
            type="upsell"
            :left-icon="['fak', 'upgrade']"
            class="w-full mx-auto"
            @click="
              handleBillingInteraction({
                talkToSalesReason: 'Upgrade to Enterprise',
                event_site:
                  EVENT_SITES.EXPLORE_PLANS_SUPERNODE_BUTTON /* TODO, update these */,
              })
            "
          />
        </template>
      </BillingPlanComparisonCard>
    </template>
  </BittsLayout>
</template>
<script setup lang="ts">
import { BittsButton, BittsIcon, BittsLayout } from '@crossbeam/bitts';
import { EVENT_SITES, PLAN_AND_BILLING_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import BillingPlanComparisonCard from '@/components/billing/BillingPlanComparisonCard.vue';

import useBilling from '@/composables/useBilling';
import useSeats from '@/composables/useSeats';
import { useBillingStore } from '@/stores';
import { BillingPeriod } from '@/types/billing';

const { isOnTrial = false } = defineProps<{
  isOnTrial?: boolean;
}>();

const emit = defineEmits<(e: 'open-add-seats-modal') => void>();

const router = useRouter();
const billingStore = useBillingStore();
const { handleBillingInteraction } = useBilling();
const { blockSalesSeatsPPV5 } = useSeats();

const {
  isConnectorTier,
  hasSubscription,
  isSubscriptionCancelled,
  isLegacyConnectorPPv5,
  isSelfServeLegacy,
  isNotPaying,
  hasPaymentIssue,
} = storeToRefs(billingStore);

const options = [
  { value: BillingPeriod.Year },
  { value: BillingPeriod.Month, label: 'Pay monthly*' },
];

const orderedSlots = computed(() => {
  if (isNotPaying.value) return ['explorer', 'connector', 'supernode'];
  return ['connector', 'supernode', 'enterprise'];
});

const connectorButtonText = computed(() => {
  if (isNotPaying.value) return 'Upgrade to Connector';
  return hasSubscription.value && !isSubscriptionCancelled.value
    ? 'Add seats'
    : 'Current plan';
});
const connectorButtonIcon = computed<BittsIcon>(() => {
  if (
    isNotPaying.value ||
    !hasSubscription.value ||
    isSubscriptionCancelled.value
  ) {
    return null;
  } else if (hasSubscription.value && !isSubscriptionCancelled.value) {
    return ['fak', 'add'];
  }

  return ['fak', 'upgrade'];
});

function handleConnectorAction() {
  if (isSubscriptionCancelled.value) {
    router.push({ name: 'restore-plan' });
    return;
  }
  if (
    hasSubscription.value &&
    hasPaymentIssue.value &&
    connectorButtonText.value === 'Add seats'
  ) {
    return;
  }
  if (isConnectorTier.value && !isOnTrial && blockSalesSeatsPPV5.value) {
    emit('open-add-seats-modal');
    handleBillingInteraction(
      {
        event_site: EVENT_SITES.EXPLORE_PLANS_FREE_TIER_BUTTON,
        period: option.value,
      },
      null,
      true,
      null,
      true,
    );
    return;
  }
  handleBillingInteraction(
    {
      event_site: EVENT_SITES.EXPLORE_PLANS_FREE_TIER_BUTTON,
      period: option.value,
    },
    null,
    true,
  );
}

const initialValue =
  !isConnectorTier.value || !billingStore.isMonthly
    ? options[0]?.value
    : options[1]?.value;
const option = ref<BillingPeriod>(initialValue as BillingPeriod);
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-explore-billing-plans {
  @apply flex justify-between items-center mb-16;
  .c-explore-billing-plans__title {
    @apply text-neutral-text-strong text-lg font-bold;
  }
}
</style>
